import { Button, EmptyState, FieldBlock, Input, LoadingState, useToast } from '@orbind/ui'
import { useEffect, useState } from 'react'
import { CORES_DOS_PAINEIS, FUNDOS, tomDaCor, type IdDoFundo } from './PersonalizarComunicacao.tsx'
import { useEstilosDoChat, type EstiloDoChat } from './useChat.ts'
import './meus-estilos.css'

/**
 * "Meus Estilos": combinações nomeadas de aparência da Comunicação — cor dos
 * painéis, papel de parede e cor dos 2 balões.
 *
 * Mora DENTRO do próprio balão da engrenagem (correção do usuário,
 * 2026-09-17: *"eu quero que dentro daquele próprio balão que tem ali, abre a
 * questão de novo estilo… não quero que abra outra aba em cima de outra aba"*).
 * Por isso aqui não há Modal nenhum: são vistas que trocam no lugar do conteúdo
 * do popover, com um voltar no topo.
 *
 * Enquanto a pessoa mexe nas opções, a conversa atrás muda AO VIVO (`onPrevia`)
 * — ela vê o resultado antes de salvar. Sair sem salvar devolve o estilo ativo.
 */

export type RascunhoDeEstilo = {
  name: string
  backgroundColor: string | null
  wallpaper: IdDoFundo
  myBubbleColor: string | null
  otherBubbleColor: string | null
}

const RASCUNHO_VAZIO: RascunhoDeEstilo = {
  name: '',
  backgroundColor: null,
  wallpaper: 'orbind',
  myBubbleColor: null,
  otherBubbleColor: null,
}

/** Fileira de amostras de cor (predefinidas + livre), a mesma da engrenagem. */
function SeletorDeCor({
  rotulo,
  cor,
  onCor,
}: {
  rotulo: string
  cor: string | null
  onCor: (cor: string | null) => void
}) {
  const livre = cor !== null && !CORES_DOS_PAINEIS.some((c) => c.cor?.toLowerCase() === cor.toLowerCase())
  return (
    <FieldBlock label={rotulo}>
      {() => (
        <div className="chset__cores" role="radiogroup" aria-label={rotulo}>
          {CORES_DOS_PAINEIS.map((opcao) => {
            const marcada =
              (opcao.cor ?? null)?.toLowerCase() === cor?.toLowerCase() || (opcao.cor === null && cor === null)
            return (
              <button
                key={opcao.rotulo}
                type="button"
                role="radio"
                aria-checked={marcada}
                aria-label={opcao.rotulo}
                title={opcao.rotulo}
                className={`chset__cor${marcada ? ' chset__cor--marcada' : ''}${
                  opcao.cor === null ? ' chset__cor--tema' : ''
                }`}
                style={opcao.cor ? { background: opcao.cor } : undefined}
                onClick={() => onCor(opcao.cor)}
              >
                {marcada ? (
                  <i
                    className="fas fa-check"
                    aria-hidden
                    style={{ color: opcao.cor && tomDaCor(opcao.cor) === 'escura' ? '#fff' : undefined }}
                  />
                ) : null}
              </button>
            )
          })}
          <label
            className={`chset__cor chset__cor--livre${livre ? ' chset__cor--marcada' : ''}`}
            title="Escolher outra cor"
            style={livre ? { background: cor! } : undefined}
          >
            <input
              type="color"
              aria-label="Escolher outra cor"
              value={cor ?? '#eef3ff'}
              onChange={(e) => onCor(e.target.value)}
            />
            <i
              className={livre ? 'fas fa-check' : 'fas fa-eye-dropper'}
              aria-hidden
              style={{ color: livre && tomDaCor(cor!) === 'escura' ? '#fff' : undefined }}
            />
          </label>
        </div>
      )}
    </FieldBlock>
  )
}

/**
 * O painel inteiro (lista + edição) para entrar no lugar do conteúdo do
 * popover. `onVoltar` devolve o popover à tela de personalizar.
 */
export function PainelDeEstilos({
  onVoltar,
  onPrevia,
}: {
  onVoltar: () => void
  /** Chamado a cada mexida no rascunho; `null` = mostrar o estilo ativo de novo. */
  onPrevia: (previa: RascunhoDeEstilo | null) => void
}) {
  const toast = useToast()
  const { presets, criar, atualizar, excluir, aplicar } = useEstilosDoChat()
  /** `null` = lista; `'novo'` = criando; preset = editando aquele. */
  const [editando, setEditando] = useState<EstiloDoChat | 'novo' | null>(null)
  const [rascunho, setRascunho] = useState<RascunhoDeEstilo>(RASCUNHO_VAZIO)

  // A conversa atrás acompanha o rascunho; sair da edição (ou fechar o popover)
  // devolve o estilo ativo.
  useEffect(() => {
    onPrevia(editando !== null ? rascunho : null)
  }, [editando, rascunho, onPrevia])
  useEffect(() => () => onPrevia(null), [onPrevia])

  const lista = presets.data ?? []
  const salvando = criar.isPending || atualizar.isPending
  const ehNovo = editando === 'novo'
  const valido = rascunho.name.trim().length >= 1 && !salvando

  const abrirNovo = () => {
    setRascunho(RASCUNHO_VAZIO)
    setEditando('novo')
  }

  const abrirEdicao = (preset: EstiloDoChat) => {
    setRascunho({
      name: preset.name,
      backgroundColor: preset.backgroundColor,
      wallpaper: (FUNDOS.some((f) => f.id === preset.wallpaper) ? preset.wallpaper : 'orbind') as IdDoFundo,
      myBubbleColor: preset.myBubbleColor,
      otherBubbleColor: preset.otherBubbleColor,
    })
    setEditando(preset)
  }

  const salvar = () => {
    const dados = {
      name: rascunho.name.trim(),
      backgroundColor: rascunho.backgroundColor,
      wallpaper: rascunho.wallpaper,
      myBubbleColor: rascunho.myBubbleColor,
      otherBubbleColor: rascunho.otherBubbleColor,
    }
    const depois = {
      onSuccess: () => {
        setEditando(null)
        toast.push({ tone: 'success', message: 'Estilo salvo.' })
      },
      onError: () => toast.push({ tone: 'danger', message: 'Não foi possível salvar o estilo.' }),
    }
    if (editando !== 'novo' && editando) atualizar.mutate({ id: editando.id, ...dados }, depois)
    else criar.mutate(dados, depois)
  }

  /* ------------------------------------------------------- edição --- */

  if (editando !== null) {
    return (
      <div className="chset">
        <div className="chset__topo">
          <button type="button" className="chset__voltar" aria-label="Voltar" onClick={() => setEditando(null)}>
            <i className="fas fa-arrow-left" aria-hidden />
          </button>
          <span className="ch-personalizar__titulo">{ehNovo ? 'Novo estilo' : 'Editar estilo'}</span>
        </div>

        <div className="chset__form">
          <FieldBlock label="Nome do estilo" required>
            {({ id }) => (
              <Input
                id={id}
                value={rascunho.name}
                maxLength={80}
                placeholder="Ex.: Modo noturno, Trabalho…"
                onChange={(e) => setRascunho((r) => ({ ...r, name: e.target.value }))}
              />
            )}
          </FieldBlock>

          <SeletorDeCor
            rotulo="Cor dos painéis"
            cor={rascunho.backgroundColor}
            onCor={(cor) => setRascunho((r) => ({ ...r, backgroundColor: cor }))}
          />

          <FieldBlock label="Papel de parede">
            {() => (
              <div className="chset__fundos" role="radiogroup" aria-label="Papel de parede">
                {FUNDOS.map((opcao) => (
                  <button
                    key={opcao.id}
                    type="button"
                    role="radio"
                    aria-checked={rascunho.wallpaper === opcao.id}
                    className={`chset__fundo${rascunho.wallpaper === opcao.id ? ' chset__fundo--marcado' : ''}`}
                    onClick={() => setRascunho((r) => ({ ...r, wallpaper: opcao.id }))}
                  >
                    <span className={`ch-fundo__amostra ch-fundo ch-fundo--${opcao.id}`} aria-hidden />
                    {opcao.rotulo}
                  </button>
                ))}
              </div>
            )}
          </FieldBlock>

          <SeletorDeCor
            rotulo="Cor do meu balão"
            cor={rascunho.myBubbleColor}
            onCor={(cor) => setRascunho((r) => ({ ...r, myBubbleColor: cor }))}
          />
          <SeletorDeCor
            rotulo="Cor do balão da outra pessoa"
            cor={rascunho.otherBubbleColor}
            onCor={(cor) => setRascunho((r) => ({ ...r, otherBubbleColor: cor }))}
          />
        </div>

        <div className="chset__rodape">
          {/* Destrutiva sozinha à esquerda — o padrão de rodapé do projeto. */}
          <span>
            {!ehNovo ? (
              <Button
                variant="ghost"
                size="sm"
                icon="far fa-trash-alt"
                disabled={excluir.isPending}
                onClick={() =>
                  excluir.mutate((editando as EstiloDoChat).id, {
                    onSuccess: () => {
                      setEditando(null)
                      toast.push({ tone: 'success', message: 'Estilo excluído.' })
                    },
                    onError: () => toast.push({ tone: 'danger', message: 'Não foi possível excluir o estilo.' }),
                  })
                }
              >
                Excluir
              </Button>
            ) : null}
          </span>
          <span className="chset__rodape-direita">
            <Button variant="ghost" size="sm" onClick={() => setEditando(null)}>
              Cancelar
            </Button>
            <Button variant="primary" size="sm" disabled={!valido} loading={salvando} onClick={salvar}>
              Salvar
            </Button>
          </span>
        </div>
      </div>
    )
  }

  /* -------------------------------------------------------- lista --- */

  return (
    <div className="chset">
      <div className="chset__topo">
        <button type="button" className="chset__voltar" aria-label="Voltar" onClick={onVoltar}>
          <i className="fas fa-arrow-left" aria-hidden />
        </button>
        <span className="ch-personalizar__titulo">Meus Estilos</span>
      </div>

      {presets.isPending ? (
        <LoadingState rows={2} label="Carregando seus estilos" />
      ) : presets.isError ? (
        <EmptyState
          icon="fas fa-palette"
          title="Não deu para carregar"
          description="Verifique a conexão e tente de novo."
          action={
            <Button variant="secondary" size="sm" icon="fas fa-redo" onClick={() => void presets.refetch()}>
              Tentar de novo
            </Button>
          }
        />
      ) : lista.length === 0 ? (
        <span className="chset__vazio">
          Nenhum estilo salvo ainda. Monte uma combinação, dê um nome e aplique quando quiser.
        </span>
      ) : (
        <ul className="chset__lista">
          {lista.map((preset) => (
            <li key={preset.id} className={`chset__item${preset.isActive ? ' chset__item--ativo' : ''}`}>
              <button
                type="button"
                className="chset__preview"
                aria-label={`Editar ${preset.name}`}
                onClick={() => abrirEdicao(preset)}
              >
                <span className="chset__preview-fundo" style={{ background: preset.backgroundColor ?? 'var(--ob-surface)' }} />
                <span className="chset__preview-baloes">
                  <span className="chset__preview-balao" style={{ background: preset.otherBubbleColor ?? 'var(--ob-surface)' }} />
                  <span className="chset__preview-balao" style={{ background: preset.myBubbleColor ?? 'var(--ob-primary-tint)' }} />
                </span>
              </button>
              <div className="chset__item-info">
                <span className="chset__item-nome">{preset.name}</span>
                {/* Nunca só por cor: o estado em uso vai escrito. */}
                {preset.isActive ? <span className="chset__item-tag">Em uso</span> : null}
              </div>
              <div className="chset__item-acoes">
                {!preset.isActive ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={aplicar.isPending}
                    onClick={() =>
                      aplicar.mutate(preset.id, {
                        onSuccess: () => toast.push({ tone: 'success', message: `Estilo “${preset.name}” aplicado.` }),
                        onError: () => toast.push({ tone: 'danger', message: 'Não foi possível aplicar o estilo.' }),
                      })
                    }
                  >
                    Aplicar
                  </Button>
                ) : null}
                <Button variant="ghost" size="sm" onClick={() => abrirEdicao(preset)}>
                  Editar
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button variant="primary" size="sm" icon="fas fa-plus" className="chset__novo" onClick={abrirNovo}>
        Novo estilo
      </Button>
    </div>
  )
}
